import React from 'react'

import { Box, Grid, Switch, Typography } from '@mui/material'
import FormikTextField from '../../components/ui-widgets/FormikTextField'
import { CountryChip } from '../../components/chips/CountryChip'
import { RegistrationStatusChip } from '../../components/chips/RegistrationStatusChip'
import { TwoFactorStatusChip } from '../../components/chips/TwoFactorStatusChip'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import {
  useLegacyApiAddLegacyRoleMutation,
  useLegacyApiDeleteLegacyRoleMutation,
  useLegacyApiDisableMemberMutation,
  useLegacyApiDisableTwoFAMutation,
  useLegacyApiToggleVIPAccessMutation
} from '../../../api-services/asset-holdings/assetHolderApi'
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'
import { useAppDispatch } from '../../../app/hooks'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { type FormikContextType, useFormikContext } from 'formik'
import {
  type AssetHolderRole,
  type AssetHolderUpdateDto
} from '../../../api-schema-types/asset-holdings/AssetHolderCrudDtos'
import { DataGrid, type GridColumns, type GridRenderCellParams } from '@mui/x-data-grid'
import { KYCStatusChip } from '../../components/chips/KYCStatusChip'
import RemoveUsCitizenComponent from './RemoveUsCitizenComponent'
import LockResetIcon from '@mui/icons-material/LockReset'
import MemberLegacyRolePill from '../../../views/components/shared/MemberLegacyRolePill'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import BlockIcon from '@mui/icons-material/Block'
import { type EditAssetHolderProps } from './EditAssetHolderForm'
import { formatDateTimeAsApiFormat } from '../../../utility/formatters/DateFormatter'
import UpdateEmailAddressComponent from './UpdateEmailAddressComponent'

const EditAssetHolderDetailsAndRolesView: React.FC<EditAssetHolderProps> = ({
  assetHolder
}: EditAssetHolderProps) => {
  const dispatch = useAppDispatch()
  const formikProps: FormikContextType<AssetHolderUpdateDto> = useFormikContext()

  const [apiDisableMemberTrigger] = useLegacyApiDisableMemberMutation()
  const [apiDeleteRoleTrigger] = useLegacyApiDeleteLegacyRoleMutation()
  const [apiAddRoleTrigger] = useLegacyApiAddLegacyRoleMutation()
  const [apiToggleVIPAccessTrigger] = useLegacyApiToggleVIPAccessMutation()
  const [apiDisableTwoFATrigger] = useLegacyApiDisableTwoFAMutation()

  const isDetailLocked = (): boolean => {
    return !assetHolder.overrideLockRequested && true
  }

  const handleAddInstitutionalRoleForMember = async (): Promise<void> => {
    const choice = window.confirm(
      'Are you sure you want to add the INSTITUTION role?'
    )
    if (choice) {
      apiAddRoleTrigger({ assetHolderId: assetHolder.assetHolderId, legacyRole: 'INSTITUTION' })
        .unwrap()
        .then((triggerResult) => {
          dispatch(setNotification({ open: true, severity: 'success', message: 'INSTITUTIONAL role added to Member.' }))
        })
        .catch((err: any) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
    }
  }

  const handleRemoveLegacyRoleForMember = async (selectedMemberRole: AssetHolderRole): Promise<void> => {
    const choice = window.confirm(
      'Are you sure you want to delete the role?'
    )
    if (choice) {
      apiDeleteRoleTrigger({ assetHolderId: assetHolder.assetHolderId, legacyRole: selectedMemberRole.legacyRole })
        .unwrap()
        .then((triggerResult) => {
          dispatch(setNotification({ open: true, severity: 'success', message: 'INSTITUTIONAL role deleted.' }))
        })
        .catch((err: any) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
    }
  }

  const handleVIPToggle = async (): Promise<void> => {
    const choice = window.confirm(
      assetHolder.legacyEarlyAccess ? 'Are you sure you want to revoke this members VIP access?' : 'Are you sure you want to grant this member VIP access?'
    )
    if (choice) {
      apiToggleVIPAccessTrigger({ assetHolderId: assetHolder.assetHolderId })
        .unwrap()
        .then((triggerResult) => {
          dispatch(setNotification({ open: true, severity: 'success', message: 'VIP Access updated.' }))
        })
        .catch((err: any) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
    }
  }

  const handleDisableTwoFA = async (): Promise<void> => {
    const choice = window.confirm(
      'Are you sure you want to disable 2FA for this member?'
    )
    if (choice) {
      apiDisableTwoFATrigger({ assetHolderId: assetHolder.assetHolderId })
        .unwrap()
        .then((triggerResult) => {
          dispatch(setNotification({ open: true, severity: 'success', message: '2FA disabled.' }))
        })
        .catch((err: any) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
    }
  }

  const handleDisablingMember = async (): Promise<void> => {
    const choice = window.confirm(
      'Are you sure you want to disable the member?'
    )
    if (choice && assetHolder != null) {
      apiDisableMemberTrigger({ assetHolderId: assetHolder.assetHolderId })
        .unwrap()
        .then(() => {
          dispatch(setNotification({ open: true, severity: 'success', message: 'Member has been disabled.' }))
        })
        .catch((err: any) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
    }
  }

  const legacyRolesColumns: GridColumns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 100,
      editable: false
    },
    {
      field: 'legacyRole',
      headerName: 'Role',
      width: 200,
      valueGetter: (params: any) => params.row.id,
      renderCell: (params: GridRenderCellParams) => {
        return (
            <MemberLegacyRolePill role={params.row.legacyRole} />
        )
      }
    },
    {
      field: 'editId',
      headerName: 'DELETE',
      width: 200,
      valueGetter: (params: any) => params.row.id,
      renderCell: (params: GridRenderCellParams) => {
        return (
            <div>
              <Button variant="text" color={'error'} onClick={async () => { await handleRemoveLegacyRoleForMember(params.row) }} startIcon={<DeleteForeverIcon />} disabled={params.row.legacyRole !== 'INSTITUTION'} />
            </div>
        )
      }
    }
  ]
  const foundInstitutionalRole = assetHolder.assetHolderRoles.find((role) => {
    return role.legacyRole === 'INSTITUTION'
  })

  const renderLegacyRoleSection = (): JSX.Element => {
    return (
        <React.Fragment>
          <Grid container spacing={4} >
            <Grid item md={6}>
              <DataGrid rows={assetHolder.assetHolderRoles} columns={legacyRolesColumns} autoHeight={true} pageSize={10} rowsPerPageOptions={[10]} />
            </Grid>
          </Grid>
        </React.Fragment>
    )
  }
  return (
    <>
        <Box sx={{ pb: 4 }}>
          <h3>Detail</h3>
          <Grid container spacing={4} >

            {/* ==================================================================================================== */}

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'legacyFirstName'} label={'First Name'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            <Grid item md={6}>
              <FormikTextField formikProps={formikProps} fieldName={'legacyLastName'} label={'Last Name'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'partyId'} label={'Party ID'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            {/* ==================================================================================================== */}

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'legacyDob'} label={'Date Of Birth'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            <Grid item md={4}>
              <FormikTextField formikProps={formikProps} fieldName={'legacyEmailAddress'} label={'Email'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            <Grid item md={5}>
              <FormikTextField formikProps={formikProps} fieldName={'websiteUrl'} label={'Website Url'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            {/* ==================================================================================================== */}

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'dateRegistered'} label={'Date Registered'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            <Grid item md={3}>
              <Typography variant={'body2'}>Registration State</Typography>
              <RegistrationStatusChip registrationState={assetHolder.registrationState} />
            </Grid>

            <Grid item md={1}>
              <Typography variant={'body2'}>VIP Member</Typography>
              <Switch checked={assetHolder.legacyEarlyAccess} color="secondary" onChange={handleVIPToggle}/>
            </Grid>

            <Grid item md={2}>
              <Typography variant={'body2'}>2FA Status</Typography>
              <TwoFactorStatusChip twoFactorStatus={assetHolder.twoFAStatus} />
            </Grid>

            <Grid item md={2}>
              <Typography variant={'body2'}>KYC Status</Typography>
              <KYCStatusChip kycStatus={assetHolder.latestLegacyKycResult?.status} />
            </Grid>

            {/* ==================================================================================================== */}

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'legacyAddress'} label={'Address'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'legacyCity'} label={'City'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            <Grid item md={3}>
              <FormikTextField formikProps={formikProps} fieldName={'legacyPostalCode'} label={'Postal Code'} type={'text'} locked={isDetailLocked()}/>
            </Grid>

            <Grid item md={3}>
              <Typography variant={'body2'}>Country</Typography>
              <CountryChip countryCode={assetHolder.legacyCountry} />
            </Grid>

            {/* ==================================================================================================== */}

            <Grid item md={1}>
              <Typography variant={'body2'}>Citizen</Typography>
              <Chip label={assetHolder.usCitizenStatus.isUsCitizen ? 'US Citizen' : 'Non-US Citizen'} color={'default'} variant="outlined" />
            </Grid>

            {assetHolder.usCitizenStatus.isUsCitizen && (
              <Grid item md={2}>
                <Typography variant={'body2'}>US Citizen</Typography>
                <Chip label={assetHolder.usCitizenStatus.isOnCitizenWhitelist ? 'US Whitelist' : 'Not on whielist'} color={'default'} variant="outlined" />
              </Grid>
            )}

            <Grid item md={2}>
              <Typography variant={'body2'}>Last Login At</Typography>
              <Chip label={(assetHolder.lastLoginAt != null) ? formatDateTimeAsApiFormat(new Date(assetHolder.lastLoginAt)) : '-'} color={'default'} variant="outlined" />
            </Grid>

            <Grid item md={6}>
            &nbsp;
            </Grid>
          </Grid>

        </Box>

        {/* ==================================================================================================== */}

        <Box sx={{ pb: 4 }}>
            <Grid container spacing={4}>
              <Grid item md={7}>
                <Button
                    onClick={handleAddInstitutionalRoleForMember}
                    disabled={foundInstitutionalRole !== undefined}
                    startIcon={<BookmarkAddIcon />}
                    variant={'outlined'}
                    sx={{ mr: 2 }}
                >
                  Add INSTITUTION role
                </Button>

                <UpdateEmailAddressComponent />

                <Button
                  onClick={handleDisableTwoFA}
                  disabled={!assetHolder.twoFAStatus}
                  startIcon={<LockResetIcon />}
                  variant={'outlined'}
                  sx={{ ml: 2 }}
                >
                  Disable 2FA
                </Button>
              </Grid>

              <Grid item md={5} textAlign={'right'}>

                <RemoveUsCitizenComponent assetHolder={assetHolder}></RemoveUsCitizenComponent>

                <Button variant={'outlined'}
                        color='error'
                        type="button"
                        disabled={assetHolder?.legacyEmailAddress.startsWith('deleted_')}
                        onClick={handleDisablingMember}
                        sx={{ ml: 2 }}>
                  <BlockIcon/>Disable Member
                </Button>
              </Grid>
            </Grid>
          <h3>Roles</h3>
          {renderLegacyRoleSection()}
        </Box>

        {/* ==================================================================================================== */}

    </>
  )
}

export default EditAssetHolderDetailsAndRolesView
